import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Check, X, Send, Square, Plus } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import API_ENDPOINTS from '../../config/api';

/**
 * Kipupiste — the pre-work entry that starts a map from what hurts
 * (specs/impact-path-v2-prd.md §4i).
 *
 * One question, the participant's answer, and then Kysy: at most five
 * questions, facts and feelings in turn. After each answer the AI hands back
 * what it heard as candidates, and the participant keeps or drops each one.
 * Only a kept candidate reaches the map. The AI never writes to it.
 */

const Card = styled.div`
  background: #141821;
  border: 1px solid #232a36;
  border-radius: 10px;
  padding: 1.25rem 1.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Question = styled.p`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1.35;
`;

const Muted = styled.p`
  margin: 0;
  color: #98a2b3;
  line-height: 1.5;
`;

const Pain = styled.div`
  border-left: 3px solid #EF5350;
  padding: 0.4rem 0 0.4rem 0.9rem;
  font-size: 1.05rem;
  font-weight: 600;
`;

const Bubble = styled.div`
  padding: 0.75rem 0.95rem;
  border-radius: 10px;
  line-height: 1.5;
  max-width: 92%;
  ${(p) => (p.$mine
    ? 'align-self: flex-end; background: #1f2a3d; border: 1px solid #2c3a52;'
    : 'align-self: flex-start; background: #181d28; border: 1px solid #232a36;')}
`;

const Thread = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Lens = styled.span`
  display: inline-block;
  margin-bottom: 0.3rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #7b8794;
`;

const Candidate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  border: 1px dashed ${(p) => (p.$kind === 'blocker' ? '#EF5350' : '#5B9DD9')};
  opacity: ${(p) => (p.$decision === 'dropped' ? 0.45 : 1)};
`;

const Kind = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${(p) => (p.$kind === 'blocker' ? '#FF8A85' : '#8ec1f0')};
  white-space: nowrap;
`;

const CandidateText = styled.span`
  flex: 1 1 12rem;
`;

// A root cause: the kept cause this candidate sits behind.
const Behind = styled.span`
  display: block;
  font-size: 0.8rem;
  color: #7b8794;
  margin-top: 0.15rem;
`;

const Small = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${(p) => (p.$keep ? '#1f3d2a' : '#2a1f22')};
  border: 1px solid ${(p) => (p.$keep ? '#2f6b45' : '#5a2c31')};
  &:disabled { opacity: 0.5; cursor: default; }
`;

const Decided = styled.span`
  font-size: 0.85rem;
  color: #98a2b3;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 5.5rem;
  resize: vertical;
  background: #0f131b;
  color: #e8ecf2;
  border: 1px solid #2c3444;
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
  font: inherit;
  line-height: 1.5;
  &:focus { outline: none; border-color: #5B9DD9; }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  background: ${(p) => (p.$secondary ? 'transparent' : '#5B9DD9')};
  color: ${(p) => (p.$secondary ? '#98a2b3' : '#0b0d12')};
  border: 1px solid ${(p) => (p.$secondary ? '#2c3444' : '#5B9DD9')};
  &:disabled { opacity: 0.5; cursor: default; }
`;

const MAX_PAIN_POINTS = 3;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ListItem = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$active ? '#3a4457' : '#232a36')};
  background: ${(p) => (p.$active ? '#181d28' : 'transparent')};
  &:hover { border-color: #3a4457; }
`;

const ListText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const Chip = styled.button`
  all: unset;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$selected ? '#6fd3c8' : '#2c3444')};
  color: ${(p) => (p.$selected ? '#6fd3c8' : '#c9d1dc')};
  background: ${(p) => (p.$selected ? 'rgba(111, 211, 200, 0.12)' : 'transparent')};
  &:disabled { opacity: 0.5; cursor: default; }
`;

const ErrorLine = styled.p`
  margin: 0;
  color: #FF8A85;
`;

async function call(url, body) {
  const res = await fetch(url, {
    method: body ? 'POST' : 'GET',
    credentials: 'include',
    // Ask for JSON, so an auth or server failure comes back as a message
    // rather than the backend's HTML login page and a bare "HTTP 401".
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

/**
 * @param {object} props
 * @param {string|null} [props.interviewId] show this one pain point only — the
 *   grouped card on the list opens one at a time; no list, no "add another".
 * @param {number|null} [props.taskIndex] which Kipupiste question (index in
 *   settings.preWork) a new pain point answers.
 * @param {(interview: object) => void} [props.onChange] every saved turn, so
 *   the card around this can open Peilaus the moment the interview is done.
 */
export default function Kipupiste({ instanceId, task, initialAdding = false, interviewId = null, taskIndex = null, readOnly = false, onChange = null }) {
  const { t } = useTranslation();
  const [interviews, setInterviews] = useState(null);
  const [selectedId, setSelectedId] = useState(interviewId);
  const [adding, setAdding] = useState(initialAdding);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);
  const max = Number(task.max) > 0 ? Number(task.max) : MAX_PAIN_POINTS;

  // The scenario a pain point belongs to is the participant's choice. The AI
  // proposes one as they write; a tap on another chip overrides it and the
  // proposal stops moving the choice.
  const [scenarios, setScenarios] = useState([]);
  const [scenarioId, setScenarioId] = useState(null);
  const [suggested, setSuggested] = useState({ id: null, why: '' });
  const [scenarioTouched, setScenarioTouched] = useState(false);
  // The reason under the chips follows the chosen chip: the AI's own pick
  // comes with one; another choice asks for one on the spot.
  const [whyFor, setWhyFor] = useState({});
  const chooseScenario = (id) => {
    setScenarioId(id);
    setScenarioTouched(true);
    if (id === suggested.id || whyFor[id] || draft.trim().length < 12) return;
    setWhyFor((prev) => ({ ...prev, [id]: '…' }));
    call(API_ENDPOINTS.ME_KYSY_SUGGEST_SCENARIO(), { instanceId, text: draft, scenarioId: id })
      .then((d) => setWhyFor((prev) => ({ ...prev, [id]: d.why || '' })))
      .catch(() => setWhyFor((prev) => ({ ...prev, [id]: '' })));
  };
  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.INSTANCE_SCENARIOS(instanceId))
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setScenarios(Array.isArray(d) ? d : (d.scenarios || [])); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [instanceId]);
  useEffect(() => {
    if (draft.trim().length < 12 || scenarios.length < 2) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      call(API_ENDPOINTS.ME_KYSY_SUGGEST_SCENARIO(), { instanceId, text: draft })
        .then((d) => {
          if (cancelled || !d.suggestedId) return;
          setSuggested({ id: d.suggestedId, why: d.why || '' });
          setWhyFor({});
          setScenarioId((current) => (scenarioTouched ? current : d.suggestedId));
        })
        .catch(() => {});
    }, 700);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [draft, instanceId, scenarios.length, scenarioTouched]);

  useEffect(() => {
    let cancelled = false;
    call(`${API_ENDPOINTS.ME_KYSY()}?instanceId=${encodeURIComponent(instanceId)}`)
      .then((d) => { if (!cancelled) setInterviews(d.interviews || []); })
      .catch((e) => { if (!cancelled) { setInterviews([]); setError(e.message); } });
    return () => { cancelled = true; };
  }, [instanceId]);

  // Only this question's pain points count toward its cap; rows from before
  // the question index existed belong to the first question.
  const mineFor = (i) => taskIndex === null || i.taskIndex === taskIndex || (i.taskIndex == null && taskIndex === 0);
  const all = (interviews || []).filter(mineFor);
  const openOne = all.find((i) => i.status === 'open') || null;
  const pinned = interviewId ? (interviews || []).find((i) => i.id === interviewId) || null : null;
  // A link may name a pain point that no longer exists (deleted, or the
  // instance was reset). Then the page is the ordinary one, not a crash.
  const pinnedOnly = !!pinned;
  // Pinned to one pain point: that one, nothing else. Otherwise the one
  // picked from the list wins; otherwise the open one, so a person lands in
  // their unfinished conversation; otherwise the latest.
  const interview = pinned || all.find((i) => i.id === selectedId) || openOne || all[all.length - 1] || null;
  const showForm = !pinnedOnly && (!interview || (adding && !openOne && all.length < max));

  // Follow the conversation while it is going; a finished one opened from
  // its sub-card stays where the person is.
  useEffect(() => {
    if (interview?.status !== 'open') return;
    endRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [interview?.turns?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn) => {
    setBusy(true); setError(null);
    try {
      const { interview: next } = await fn();
      setInterviews((prev) => [...(prev || []).filter((i) => i.id !== next.id), next]);
      setSelectedId(next.id);
      setAdding(false);
      setDraft('');
      onChange?.(next);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const needsScenario = scenarios.length > 0 && !scenarioId;
  const start = () => run(() => call(API_ENDPOINTS.ME_KYSY(), { instanceId, text: draft, taskIndex, scenarioId }))
    .then(() => { setScenarioId(null); setSuggested({ id: null, why: '' }); setScenarioTouched(false); });
  const answer = () => run(() => call(API_ENDPOINTS.ME_KYSY_ACTION(interview.id, 'answer'), { text: draft }));
  const stop = () => run(() => call(API_ENDPOINTS.ME_KYSY_ACTION(interview.id, 'stop'), {}));
  const decide = (turn, index, keep) =>
    run(() => call(API_ENDPOINTS.ME_KYSY_ACTION(interview.id, 'decide'), { turn, index, keep }));

  if (interviews === null) return <Card><Muted>{t('me.loading')}</Muted></Card>;

  const keptOf = (i) => i.turns.flatMap((x) => x.candidates).filter((c) => c.decision === 'kept').length;

  const list = !pinnedOnly && all.length > 0 && (
    <List>
      <Lens>{t('me.kipupiste.yours', { count: all.length, max })}</Lens>
      {all.map((i) => (
        <ListItem key={i.id} $active={!showForm && interview?.id === i.id}
                  onClick={() => { setSelectedId(i.id); setAdding(false); setError(null); }}>
          <ListText>{i.painPoint}{i.scenario?.title ? <Decided> · {i.scenario.title}</Decided> : null}</ListText>
          <Decided>{i.status === 'open' ? t('me.kipupiste.inProgress') : t('me.kipupiste.keptCount', { count: keptOf(i) })}</Decided>
        </ListItem>
      ))}
    </List>
  );

  if (showForm) {
    return (
      <Card>
        {task.title && <Lens>{task.title}</Lens>}
        {list}
        <Question>{task.question || t('me.kipupiste.defaultQuestion')}</Question>
        <Muted>{t('me.kipupiste.intro')}</Muted>
        <TextArea value={draft} onChange={(e) => setDraft(e.target.value)}
                  placeholder={t('me.kipupiste.placeholder')} disabled={busy} />
        {scenarios.length > 0 && (
          <div>
            <Lens>{t('me.kipupiste.scenario')}</Lens>
            <ChipRow style={{ marginTop: '0.4rem' }}>
              {scenarios.map((sc) => (
                <Chip key={sc.id} type="button" $selected={scenarioId === sc.id} disabled={busy}
                      onClick={() => chooseScenario(sc.id)}>
                  {sc.title}{suggested.id === sc.id ? ` · ${t('me.kipupiste.aiSuggested')}` : ''}
                </Chip>
              ))}
            </ChipRow>
            {scenarioId && (scenarioId === suggested.id ? suggested.why : whyFor[scenarioId]) && (
              <Muted style={{ marginTop: '0.4rem' }}>{scenarioId === suggested.id ? suggested.why : whyFor[scenarioId]}</Muted>
            )}
          </div>
        )}
        {error && <ErrorLine>{error}</ErrorLine>}
        <Actions>
          <Button onClick={start} disabled={busy || !draft.trim() || needsScenario}>
            <Send size={15} /> {busy ? t('me.kipupiste.thinking') : t('me.kipupiste.start')}
          </Button>
          {all.length > 0 && (
            <Button $secondary onClick={() => setAdding(false)} disabled={busy}>{t('me.kipupiste.cancelNew')}</Button>
          )}
        </Actions>
      </Card>
    );
  }

  const last = interview.turns[interview.turns.length - 1];
  const open = interview.status === 'open' && last?.question && !last.answer;
  const kept = keptOf(interview);
  // Read-only after the workshop: the conversation is shown as it was, nothing is answered, kept or added.
  const canAdd = !readOnly && !pinnedOnly && !openOne && all.length < max;

  return (
    <Card>
      {task.title && !pinnedOnly && <Lens>{task.title}</Lens>}
      {all.length > 1 && list}
      {/* Pinned under a card that already names the pain point: no repeat. */}
      {!pinnedOnly && (
        <Pain>{interview.painPoint}{interview.scenario?.title ? <Decided style={{ display: 'block', marginTop: '0.2rem' }}>{interview.scenario.title}</Decided> : null}</Pain>
      )}
      <Thread>
        {interview.turns.map((turn, ti) => (
          <React.Fragment key={turn.n}>
            {turn.candidates.length > 0 && (
              <Bubble>
                <Lens>{t('me.kipupiste.heard')}</Lens>
                <Thread>
                  {turn.candidates.map((c, ci) => (
                    <Candidate key={ci} $kind={c.kind} $decision={c.decision}>
                      <Kind $kind={c.kind}>
                        {c.kind === 'blocker' ? t('me.kipupiste.blocker') : t('me.kipupiste.cause')}
                        {c.lens ? ` · ${c.lens}` : ''}
                      </Kind>
                      <CandidateText>
                        {c.text}
                        {c.causeOf && <Behind>{t('me.kipupiste.causeOf', { cause: c.causeOf })}</Behind>}
                      </CandidateText>
                      {c.decision ? (
                        <Decided>{c.decision === 'kept' ? t('me.kipupiste.kept') : t('me.kipupiste.dropped')}</Decided>
                      ) : readOnly ? null : (
                        <>
                          <Small $keep onClick={() => decide(ti, ci, true)} disabled={busy}><Check size={14} /> {t('me.kipupiste.keep')}</Small>
                          <Small onClick={() => decide(ti, ci, false)} disabled={busy}><X size={14} /> {t('me.kipupiste.drop')}</Small>
                        </>
                      )}
                    </Candidate>
                  ))}
                </Thread>
              </Bubble>
            )}
            {turn.feelings && <Bubble><Muted>{turn.feelings}</Muted></Bubble>}
            {turn.question && (
              <Bubble>
                {turn.lens && <Lens>{turn.lens}</Lens>}
                <div>{turn.question}</div>
              </Bubble>
            )}
            {turn.answer && <Bubble $mine>{turn.answer}</Bubble>}
            {turn.done && turn.closing && <Bubble>{turn.closing}</Bubble>}
          </React.Fragment>
        ))}
        <div ref={endRef} />
      </Thread>

      {error && <ErrorLine>{error}</ErrorLine>}

      {open && !readOnly ? (
        <>
          <TextArea value={draft} onChange={(e) => setDraft(e.target.value)}
                    placeholder={t('me.kipupiste.answerPlaceholder')} disabled={busy} />
          <Actions>
            <Button onClick={answer} disabled={busy || !draft.trim()}>
              <Send size={15} /> {busy ? t('me.kipupiste.thinking') : t('me.kipupiste.answer')}
            </Button>
            <Button $secondary onClick={stop} disabled={busy}><Square size={13} /> {t('me.kipupiste.stop')}</Button>
          </Actions>
        </>
      ) : (
        <>
          <Muted>{t('me.kipupiste.done', { count: kept })}</Muted>
          {canAdd ? (
            <Actions>
              <Button $secondary onClick={() => { setAdding(true); setError(null); }}>
                <Plus size={15} /> {t('me.kipupiste.addAnother', { count: all.length, max })}
              </Button>
            </Actions>
          ) : (
            all.length >= max && <Muted>{t('me.kipupiste.limit', { max })}</Muted>
          )}
        </>
      )}
    </Card>
  );
}
